import { nanoid } from "nanoid";
import { useCallback, useEffect, useReducer, useRef } from "react";

import { completeAttachmentUpload } from "@/lib/api/attachments";
import apiClient from "@/lib/api/client";

import type {
  FileUploaderOptions,
  ManagedFile,
  PresignedUrlResponse,
} from "./types";

const DEFAULT_MAX_FILE_SIZE = 200 * 1024 * 1024;
const DEFAULT_MAX_FILES = 150;

type FileAction =
  | {
      payload: { id: string; updates: Partial<ManagedFile> };
      type: "UPDATE_FILE";
    }
  | { payload: ManagedFile[]; type: "ADD_FILES" }
  | { payload: string; type: "REMOVE_FILE" }
  | { type: "CLEAR_ALL" };

function fileReducer(state: ManagedFile[], action: FileAction): ManagedFile[] {
  switch (action.type) {
    case "ADD_FILES": {
      return [...state, ...action.payload];
    }
    case "UPDATE_FILE": {
      return state.map((file) =>
        file.id === action.payload.id
          ? { ...file, ...action.payload.updates }
          : file,
      );
    }
    case "REMOVE_FILE": {
      return state.filter((file) => file.id !== action.payload);
    }
    case "CLEAR_ALL": {
      return [];
    }
    default: {
      return state;
    }
  }
}

/**
 * Return type for the useFileUploader hook
 */
export interface UseFileUploaderReturn {
  addFiles: (fileList: FileList | null) => void;
  clearAll: () => void;
  failedUploads: ManagedFile[];
  files: ManagedFile[];
  isUploading: boolean;
  loadExistingFiles: (
    existingFiles: Array<{
      id: number;
      mime?: string;
      name: string;
      url?: null | string;
    }>,
  ) => void;
  removeFile: (fileId: string) => void;
  retryUpload: (fileId: string) => void;
  successfulUploads: ManagedFile[];
}

export function useFileUploader(
  options: FileUploaderOptions,
): UseFileUploaderReturn {
  const {
    acceptedFileTypes,
    category,
    isMbc,
    maxFiles = DEFAULT_MAX_FILES,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    onFilesChange,
    onFileUploaded,
    onUploadComplete,
    parentId,
    parentType,
    preventDuplicates = false,
  } = options;

  const [files, dispatch] = useReducer(fileReducer, []);
  const abortControllers = useRef<Map<string, AbortController>>(new Map());
  const prevFilesRef = useRef<ManagedFile[]>([]);

  useEffect(() => {
    if (onFilesChange && files !== prevFilesRef.current) {
      onFilesChange(files);
      prevFilesRef.current = files;
    }
  }, [files, onFilesChange]);

  useEffect(() => {
    const uploading = files.filter(
      (f) => f.status === "uploading" || f.status === "pending",
    );
    const successful = files.filter(
      (f) => f.status === "success" && f.attachmentId,
    );

    if (uploading.length === 0 && successful.length > 0 && onUploadComplete) {
      const ids = successful
        .map((f) => f.attachmentId)
        .filter((id): id is number => id !== undefined);

      onUploadComplete(ids);
    }
  }, [files, onUploadComplete]);

  const uploadFile = useCallback(
    async (managedFile: ManagedFile) => {
      const abortController = new AbortController();

      abortControllers.current.set(managedFile.id, abortController);

      dispatch({
        payload: {
          id: managedFile.id,
          updates: { progress: 0, status: "uploading" },
        },
        type: "UPDATE_FILE",
      });

      try {
        if (!managedFile.file) {
          throw new Error("Cannot upload: file is missing");
        }

        const presignedData = await apiClient
          .query({
            fileName: managedFile.file.name,
            ...(isMbc ? { isMbc: true } : {}),
            parentId,
            parentType,
            size: managedFile.file.size.toString(),
            type: category,
          })
          .get("/attachments/upload-url")
          .json<PresignedUrlResponse>();

        const formData = new FormData();

        for (const [key, value] of Object.entries(presignedData.fields)) {
          formData.append(key, value as string);
        }
        formData.append("file", managedFile.file);

        await uploadWithProgress(
          presignedData.presignedUrl,
          formData,
          (progress) => {
            dispatch({
              payload: { id: managedFile.id, updates: { progress } },
              type: "UPDATE_FILE",
            });
          },
          abortController.signal,
        );

        if (isMbc) {
          await completeAttachmentUpload(presignedData.attachmentId);
        }

        dispatch({
          payload: {
            id: managedFile.id,
            updates: {
              attachmentId: presignedData.attachmentId,
              errorMessage: undefined,
              progress: 100,
              status: "success",
              // The durable (unsigned, non-expiring) object url — callers that
              // persist a file reference of their own need it, not just the id.
              url: presignedData.url,
            },
          },
          type: "UPDATE_FILE",
        });
        onFileUploaded?.(presignedData.attachmentId);
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";

        if (error instanceof Error && error.name === "AbortError") {
          return;
        }

        dispatch({
          payload: {
            id: managedFile.id,
            updates: { errorMessage, status: "error" },
          },
          type: "UPDATE_FILE",
        });
      } finally {
        abortControllers.current.delete(managedFile.id);
      }
    },
    [parentId, parentType, category, isMbc, onFileUploaded],
  );

  const addFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;

      const newFiles: ManagedFile[] = [];
      const filesToAdd = [...fileList];

      const currentCount = files.length;
      const availableSlots = maxFiles - currentCount;

      if (availableSlots <= 0) {
        console.warn(`Maximum file limit (${maxFiles}) reached`);

        return;
      }

      const filesToProcess = filesToAdd.slice(0, availableSlots);

      // Helper function to get file identifier (name + extension)
      const getFileIdentifier = (fileName: string): string =>
        fileName.toLowerCase();

      // Build a set of existing file identifiers
      const existingFileIdentifiers = preventDuplicates
        ? new Set(
            files
              .map((f) => {
                const fileName = f.file?.name || f.name;

                return fileName ? getFileIdentifier(fileName) : null;
              })
              .filter((id): id is string => id !== null),
          )
        : new Set<string>();

      for (const file of filesToProcess) {
        // Check for duplicates if preventDuplicates is enabled
        if (preventDuplicates) {
          const fileIdentifier = getFileIdentifier(file.name);

          if (existingFileIdentifiers.has(fileIdentifier)) {
            const errorFile: ManagedFile = {
              errorMessage: `A file with the name "${file.name}" already exists`,
              file,
              id: nanoid(),
              progress: 0,
              status: "error",
            };

            newFiles.push(errorFile);
            continue;
          }
          // Add to set so subsequent files in the same batch are also checked
          existingFileIdentifiers.add(fileIdentifier);
        }

        if (acceptedFileTypes && acceptedFileTypes.length > 0) {
          const ext = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;

          if (!acceptedFileTypes.includes(ext)) {
            newFiles.push({
              errorMessage: "File type not supported",
              file,
              id: nanoid(),
              isRetryable: false,
              progress: 0,
              status: "error",
            });
            continue;
          }
        }

        if (file.size > maxFileSize) {
          const errorFile: ManagedFile = {
            errorMessage: `File exceeds maximum size of ${Math.round(maxFileSize / 1024 / 1024)}MB`,
            file,
            id: nanoid(),
            progress: 0,
            status: "error",
          };

          newFiles.push(errorFile);
          continue;
        }

        const managedFile: ManagedFile = {
          file,
          id: nanoid(),
          progress: 0,
          status: "pending",
        };

        newFiles.push(managedFile);
      }

      if (newFiles.length > 0) {
        dispatch({ payload: newFiles, type: "ADD_FILES" });

        for (const file of newFiles) {
          if (file.status === "pending") {
            uploadFile(file);
          }
        }
      }
    },
    [
      acceptedFileTypes,
      files,
      maxFiles,
      maxFileSize,
      preventDuplicates,
      uploadFile,
    ],
  );

  const removeFile = useCallback((fileId: string) => {
    const controller = abortControllers.current.get(fileId);

    if (controller) {
      controller.abort();
      abortControllers.current.delete(fileId);
    }
    dispatch({ payload: fileId, type: "REMOVE_FILE" });
  }, []);

  const retryUpload = useCallback(
    (fileId: string) => {
      const file = files.find((f) => f.id === fileId);

      if (file && file.status === "error") {
        dispatch({
          payload: {
            id: fileId,
            updates: {
              errorMessage: undefined,
              progress: 0,
              status: "pending",
            },
          },
          type: "UPDATE_FILE",
        });
        uploadFile(file);
      }
    },
    [files, uploadFile],
  );

  const clearAll = useCallback(() => {
    for (const [, controller] of abortControllers.current) {
      controller.abort();
    }
    abortControllers.current.clear();
    dispatch({ type: "CLEAR_ALL" });
  }, []);

  const isUploading = files.some(
    (f) => f.status === "uploading" || f.status === "pending",
  );
  const successfulUploads = files.filter((f) => f.status === "success");
  const failedUploads = files.filter((f) => f.status === "error");

  const loadExistingFiles = useCallback(
    (
      existingFiles: Array<{
        id: number;
        mime?: string;
        name: string;
        url?: null | string;
      }>,
    ) => {
      const managedFiles: ManagedFile[] = existingFiles.map((attachment) => ({
        attachmentId: attachment.id,
        id: `existing-${attachment.id}`,
        isExisting: true,
        mime: attachment.mime,
        name: attachment.name,
        progress: 100,
        status: "success" as const,
        url: attachment.url ?? undefined,
      }));

      dispatch({ payload: managedFiles, type: "ADD_FILES" });
    },
    [],
  );

  return {
    addFiles,
    clearAll,
    failedUploads,
    files,
    isUploading,
    loadExistingFiles,
    removeFile,
    retryUpload,
    successfulUploads,
  };
}

function uploadWithProgress(
  url: string,
  formData: FormData,
  onProgress: (progress: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    signal.addEventListener("abort", () => {
      xhr.abort();
      reject(new DOMException("Aborted", "AbortError"));
    });

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100);

        onProgress(progress);
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Upload failed"));
    });

    xhr.open("POST", url);
    xhr.send(formData);
  });
}
